/**
 * MCP Config Converter
 *
 * Main orchestrator for converting various MCP server configuration formats
 * to the standard format used by the backend.
 *
 * Supports:
 * - JSON formats (wrapped, single-server, multi-server)
 * - CLI commands (Claude Code, Gemini CLI, Codex CLI)
 *
 * Extensible architecture: Add new converters by creating a file in ./converters/
 * and registering it in the converters array below.
 */

import { parseCliCommand } from './cli-parser';
import { multiServerConverter, singleServerConverter, wrappedFormatConverter } from './converters';
import { detectInputType } from './input-detector';
import type { ConversionResult, ConverterEntry, ParsedMcpServer } from './types';

/**
 * Registry of all available converters, sorted by priority (highest first)
 *
 * To add a new format:
 * 1. Create a new converter file in ./converters/
 * 2. Implement FormatDetector and FormatConverter
 * 3. Export as ConverterEntry
 * 4. Add to this array
 */
const converters: ConverterEntry[] = [
  wrappedFormatConverter,
  multiServerConverter,
  singleServerConverter,
].sort((a, b) => b.priority - a.priority);

/**
 * Attempts to parse and convert an MCP config snippet
 *
 * Supports both JSON configurations and CLI commands from Claude Code, Gemini CLI, and Codex CLI.
 *
 * @param input - The raw input string (JSON or CLI command) or object
 * @returns ConversionResult with success status, servers, and any errors
 *
 * @example
 * // Wrapped JSON format
 * const result = convertMcpConfig('{"mcpServers":{"github":{"command":"npx","args":["-y","@modelcontextprotocol/server-github"]}}}');
 * // result.servers = [{ name: 'github', config: { command: 'npx', args: [...] } }]
 *
 * @example
 * // Single server JSON format
 * const result = convertMcpConfig('{"command":"npx","args":["@playwright/mcp@latest"]}');
 * // result.servers = [{ name: 'playwright', config: { command: 'npx', args: [...] } }]
 *
 * @example
 * // Claude CLI command
 * const result = convertMcpConfig('claude mcp add --transport http --header "API_KEY: xyz" myserver https://example.com/mcp');
 * // result.servers = [{ name: 'myserver', config: { type: 'http', url: '...', headers: {...} } }]
 */
export const convertMcpConfig = (input: string | object): ConversionResult => {
  // If input is an object, skip detection and go straight to JSON parsing
  if (typeof input !== 'string') {
    return convertFromJson(input);
  }

  const trimmed = input.trim();
  if (!trimmed) {
    return {
      success: false,
      servers: [],
      error: 'Input is empty',
    };
  }

  // Detect input type
  const detection = detectInputType(trimmed);

  switch (detection.type) {
    case 'json':
      return convertFromJson(detection.normalized);

    case 'cli-command':
      return convertFromCli(detection.normalized, detection.cliTool);

    case 'unknown':
    default:
      return {
        success: false,
        servers: [],
        error:
          'Unrecognized format. Expected JSON configuration or CLI command (e.g., "claude mcp add ...").',
      };
  }
};

/**
 * Convert from JSON input (string or object)
 */
const convertFromJson = (input: string | object): ConversionResult => {
  // Step 1: Parse JSON if string
  let parsed: unknown;
  if (typeof input === 'string') {
    try {
      parsed = JSON.parse(input);
    } catch (e) {
      return {
        success: false,
        servers: [],
        error: `Invalid JSON: ${e instanceof Error ? e.message : 'Parse error'}`,
      };
    }
  } else {
    parsed = input;
  }

  // Step 2: Try each converter in priority order
  const converterResult = converters.reduce<ConversionResult | null>((acc, converter) => {
    if (acc) return acc;
    if (!converter.detect(parsed)) return null;
    try {
      const servers = converter.convert(parsed);
      if (servers.length > 0) {
        return {
          success: true,
          servers,
          detectedFormat: converter.format,
        };
      }
    } catch {
      // Converter failed, try next
    }
    return null;
  }, null);

  if (converterResult) return converterResult;

  // Step 3: No converter matched
  return {
    success: false,
    servers: [],
    error:
      'Unrecognized JSON format. Expected JSON with either "command" (for STDIO) or "url" (for HTTP) configuration.',
  };
};

/**
 * Convert from CLI command input
 */
const convertFromCli = (
  command: string,
  tool?: 'claude' | 'gemini' | 'codex',
): ConversionResult => {
  const result = parseCliCommand(command, tool);

  // Map CLI parse result to ConversionResult
  return {
    success: result.success,
    servers: result.servers,
    error: result.error,
    detectedFormat: result.success ? 'cli-command' : undefined,
    // Include warnings in error if present
    ...(result.warnings && result.warnings.length > 0 && !result.error
      ? { warnings: result.warnings }
      : {}),
  };
};

/**
 * Converts a ParsedMcpServer to the backend-expected JSON string format
 *
 * @param server - The parsed server object
 * @returns JSON string ready for the backend
 */
export const toBackendFormat = (server: ParsedMcpServer): string => {
  return JSON.stringify(server.config, null, 2);
};

/**
 * Validates that a config object has the required fields
 *
 * @param config - The config object to validate
 * @returns Object with isValid boolean and optional error message
 */
export const validateConfig = (config: unknown): { isValid: boolean; error?: string } => {
  if (typeof config !== 'object' || config === null) {
    return { isValid: false, error: 'Configuration must be a JSON object' };
  }

  const obj = config as Record<string, unknown>;
  const hasCommand = typeof obj.command === 'string' && obj.command.length > 0;
  const hasUrl = typeof obj.url === 'string' && obj.url.length > 0;

  if (!hasCommand && !hasUrl) {
    return {
      isValid: false,
      error: 'Configuration must have either "command" (for STDIO) or "url" (for HTTP)',
    };
  }

  return { isValid: true };
};
